import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

const ContactPage = () => {
    useSEO({
      title: 'Contact Veydra',
      description: 'Get in touch with Veydra. Schedule a conversation, explore partnerships, or ask a question about our simulation platform.'
    });

    return (
        <div className="min-h-screen bg-white">
            <TopNav />
            
            {/* Hero */}
            <main className="max-w-5xl mx-auto px-8 py-20 md:py-28 text-center">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Contact</p>
                <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
                    Let's talk
                </h1>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                    Whether you have a question, want to explore a partnership, or are ready to get started—we'd love to hear from you.
                </p>
            </main>

            {/* Contact Options */}
            <section className="py-16 md:py-20 px-8 bg-gray-50">
                <div className="max-w-4xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Schedule a Conversation */}
                        <div className="bg-white border border-gray-200 rounded-lg p-8 hover:border-gray-300 transition-colors flex flex-col">
                            <div className="w-10 h-10 bg-gray-900 rounded flex items-center justify-center mb-5">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-medium text-gray-900 mb-3">Schedule a conversation</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                                Book a time that works for you. We'll discuss your challenges, explore how simulation can help, and answer any questions.
                            </p>
                            <a href="/book-demo" className="mt-auto">
                                <button className="w-full bg-gray-900 text-white px-6 py-3 rounded text-sm font-medium hover:bg-gray-800 transition-colors">
                                    Book a call →
                                </button>
                            </a>
                        </div>

                        {/* Call Us */}
                        <div className="bg-white border border-gray-200 rounded-lg p-8 hover:border-gray-300 transition-colors flex flex-col">
                            <div className="w-10 h-10 bg-gray-900 rounded flex items-center justify-center mb-5">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-medium text-gray-900 mb-3">Call us directly</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                                Have an immediate question? Our team is available during business hours to help.
                            </p>
                            <a href="tel:+18885434111" className="mt-auto">
                                <button className="w-full border border-gray-900 text-gray-900 px-6 py-3 rounded text-sm font-medium hover:bg-gray-900 hover:text-white transition-colors">
                                    +1 (888) 543-4111
                                </button>
                            </a>
                        </div>
                    </div>

                    {/* Additional Contact Options */}
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Email */}
                        <div className="bg-white border border-gray-200 rounded-lg p-8 hover:border-gray-300 transition-colors">
                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center mb-5">
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Email us</h3>
                            <p className="text-gray-600 mb-4 text-sm">For general inquiries or support</p>
                            <a href="mailto:support@veydra.com" className="text-gray-900 font-medium hover:text-gray-700">
                                support@veydra.com
                            </a>
                        </div>

                        {/* Request Access */}
                        <div className="bg-white border border-gray-200 rounded-lg p-8 hover:border-gray-300 transition-colors">
                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center mb-5">
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Request platform access</h3>
                            <p className="text-gray-600 mb-4 text-sm">Already know you want to try the platform?</p>
                            <Link to="/request-access" className="text-gray-900 font-medium hover:text-gray-700">
                                Request access →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* For Specific Needs */}
            <section className="py-16 md:py-20 px-8 bg-white">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-4 tracking-tight">
                        Looking for something specific?
                    </h2>
                    <p className="text-gray-600 mb-10 max-w-2xl mx-auto">
                        We work with enterprises, consultants, and educators. Here's where to start:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Link to="/solutions" className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 hover:shadow-sm transition-all group">
                            <h3 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-gray-700">Industry Solutions</h3>
                            <p className="text-sm text-gray-600">Strategic decision support across industries</p>
                        </Link>
                        <Link to="/educators" className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 hover:shadow-sm transition-all group">
                            <h3 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-gray-700">For Educators</h3>
                            <p className="text-sm text-gray-600">Classroom tools and curriculum for teaching simulation</p>
                        </Link>
                        <Link to="/innovation" className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 hover:shadow-sm transition-all group">
                            <h3 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-gray-700">Research & Innovation</h3>
                            <p className="text-sm text-gray-600">Whitepapers and articles on simulation methodology</p>
                        </Link>
                    </div>
                </div>
            </section>
            
            <Footer />
        </div>
    );
};

export default ContactPage;
